import React, { useState } from 'react';
import { Table, Button, Space, message, Modal, Form, Input, Switch, InputNumber, Upload } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined, PictureOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../api/axios';
import MediaPickerModal from '../../components/common/MediaPickerModal';

interface AdminAffiliationDto {
    id: string;
    name?: string;
    logoUrl: string;
    linkUrl?: string;
    orderIndex: number;
    isActive: boolean;
}

export const AffiliationsList: React.FC = () => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [mediaPickerOpen, setMediaPickerOpen] = useState(false);
    const [form] = Form.useForm();
    const queryClient = useQueryClient();

    const { data: affiliations, isLoading } = useQuery({
        queryKey: ['adminAffiliations'],
        queryFn: async () => {
            const response = await apiClient.get('/admin/affiliations');
            return response.data.data;
        }
    });

    const { refetch: fetchDetail } = useQuery({
        queryKey: ['adminAffiliationDetail', editingId],
        queryFn: async () => {
            if (!editingId) return null;
            const response = await apiClient.get(`/admin/affiliations/${editingId}`);
            return response.data.data;
        },
        enabled: false
    });

    const deleteMutation = useMutation({
        mutationFn: async (id: string) => {
            await apiClient.delete(`/admin/affiliations/${id}`);
        },
        onSuccess: () => {
            message.success('Mitgliedschaft deleted successfully.');
            queryClient.invalidateQueries({ queryKey: ['adminAffiliations'] });
        },
        onError: () => {
            message.error('Failed to delete.');
        }
    });

    const saveMutation = useMutation({
        mutationFn: async (values: any) => {
            const payload = {
                id: editingId || undefined,
                name: values.name,
                logoUrl: values.logoUrl,
                linkUrl: values.linkUrl,
                orderIndex: values.orderIndex,
                isActive: values.isActive
            };

            if (editingId) {
                await apiClient.put(`/admin/affiliations/${editingId}`, payload);
            } else {
                await apiClient.post('/admin/affiliations', payload);
            }
        },
        onSuccess: () => {
            message.success('Mitgliedschaft saved successfully.');
            setIsModalVisible(false);
            form.resetFields();
            queryClient.invalidateQueries({ queryKey: ['adminAffiliations'] });
        },
        onError: (error: any) => {
            message.error(error.response?.data?.detail || error.message || 'Failed to save.');
        }
    });

    const handleEdit = async (id: string) => {
        setEditingId(id);
        const { data: detail } = await fetchDetail();
        if (detail) {
            form.setFieldsValue({
                name: detail.name,
                logoUrl: detail.logoUrl,
                linkUrl: detail.linkUrl,
                orderIndex: detail.orderIndex,
                isActive: detail.isActive
            });
            setIsModalVisible(true);
        }
    };

    const handleCreate = () => {
        setEditingId(null);
        form.resetFields();
        form.setFieldsValue({
            isActive: true,
            orderIndex: 0
        });
        setIsModalVisible(true);
    };

    const handleDelete = (id: string) => {
        Modal.confirm({
            title: 'Are you sure you want to delete this?',
            content: 'This action cannot be undone.',
            onOk: () => deleteMutation.mutate(id)
        });
    };

    const columns = [
        { 
            title: 'Logo', 
            dataIndex: 'logoUrl', 
            key: 'logoUrl',
            render: (url: string) => url ? <img src={url} alt="Logo" style={{ height: 40, objectFit: 'contain' }} /> : '-'
        },
        { title: 'Name', dataIndex: 'name', key: 'name' },
        { title: 'Order', dataIndex: 'orderIndex', key: 'orderIndex' },
        { 
            title: 'Active', 
            dataIndex: 'isActive', 
            key: 'isActive',
            render: (act: boolean) => act ? 'Yes' : 'No'
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: AdminAffiliationDto) => (
                <Space size="middle">
                    <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record.id)} />
                    <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)} />
                </Space>
            )
        }
    ];

    const [uploading, setUploading] = useState(false);

    const handleDirectUpload = async (file: File) => {
        setUploading(true);
        const formData = new FormData();
        formData.append('file', file);
        formData.append('isPublic', 'true');
        formData.append('isActive', 'true');

        try {
            const response = await apiClient.post('/admin/media/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            const dataObj = response.data?.data || response.data?.Data || response.data;
            const imgUrl = dataObj?.url || dataObj?.Url || dataObj?.filePath || dataObj?.FilePath;
            if (imgUrl) {
                form.setFieldsValue({ logoUrl: imgUrl });
                message.success('Logo erfolgreich hochgeladen');
                queryClient.invalidateQueries({ queryKey: ['adminMedia'] });
            }
        } catch (error: any) {
            message.error(error.response?.data?.message || 'Upload fehlgeschlagen');
        } finally {
            setUploading(false);
        }
        return false;
    };

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <h2>Mitgliedschaften / Affiliations</h2>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                    Create New
                </Button>
            </div>

            <Table
                dataSource={affiliations}
                columns={columns}
                rowKey="id"
                loading={isLoading}
                pagination={{ pageSize: 10 }}
            />

            <Modal
                title={editingId ? 'Edit Mitgliedschaft' : 'Create Mitgliedschaft'}
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                onOk={() => form.submit()}
                confirmLoading={saveMutation.isPending}
                destroyOnHidden
                width={600}
            >
                <Form form={form} layout="vertical" onFinish={(values) => saveMutation.mutate(values)}>
                    <Form.Item 
                        name="logoUrl" 
                        label="Logo URL (Required)" 
                        rules={[{ required: true, message: 'Logo URL ist erforderlich' }]}
                    >
                        <Input placeholder="/media/public/... oder Bild hochladen / aus Mediathek wählen" />
                    </Form.Item>

                    <Space wrap style={{ marginBottom: 16 }}>
                        <Upload beforeUpload={handleDirectUpload} showUploadList={false} maxCount={1}>
                            <Button icon={<UploadOutlined />} loading={uploading}>
                                Bild Hochladen
                            </Button>
                        </Upload>
                        <Button 
                            icon={<PictureOutlined />}
                            onClick={() => setMediaPickerOpen(true)}
                        >
                            Aus Mediathek
                        </Button>
                    </Space>

                    <Form.Item noStyle shouldUpdate={(prev, curr) => prev?.logoUrl !== curr?.logoUrl}>
                        {({ getFieldValue }) => {
                            const url = getFieldValue('logoUrl');
                            if (!url) return null;
                            return (
                                <div style={{ marginBottom: 16 }}>
                                    <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Vorschau:</div>
                                    <img 
                                        src={url} 
                                        alt="Logo Vorschau" 
                                        style={{ maxHeight: 60, maxWidth: 200, objectFit: 'contain', border: '1px solid #d9d9d9', padding: 4, borderRadius: 4, background: '#fafafa' }} 
                                    />
                                </div>
                            );
                        }}
                    </Form.Item>
                    
                    <Form.Item name="name" label="Name (Optional)">
                        <Input />
                    </Form.Item>

                    <Form.Item name="linkUrl" label="External URL / Link (Optional)">
                        <Input />
                    </Form.Item>

                    <Space size="large" style={{ display: 'flex', width: '100%' }}>
                        <Form.Item name="orderIndex" label="Order Index">
                            <InputNumber min={0} />
                        </Form.Item>
                        <Form.Item name="isActive" label="Is Active" valuePropName="checked">
                            <Switch />
                        </Form.Item>
                    </Space>
                </Form>
            </Modal>

            <MediaPickerModal
                open={mediaPickerOpen}
                onCancel={() => setMediaPickerOpen(false)}
                onSelect={(url) => {
                    form.setFieldsValue({ logoUrl: url });
                    setMediaPickerOpen(false);
                }}
            />
        </div>
    );
};
